import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { track } from "@vercel/analytics";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../AuthModal/AuthModal";
import SEOHead from "../SEO/SEOHead";
import { generateWebPageStructuredData } from "../SEO/StructuredData";
import ProductBreadcrumb from "./ProductBreadcrumb";
import ProductImportStep from "./ProductImportStep";
import ProductNextStep from "./ProductNextStep";

const ProductPage = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  heading,
  lede,
  kicker,
  importStep,
  analyticsLocation,
  structuredData,
  secondaryTo,
  secondaryLabel,
  onSecondaryClick,
  sectionHeading,
  sections = [],
  screenshot,
  ogImage,
  ogImageAlt,
  additionalStructuredData = [],
  showNextStep = true,
  children,
}) => {
  const { user } = useAuth();
  const { pathname, hash } = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const id = hash.replace("#", "");
    if (!id) return;
    const node = document.getElementById(id);
    if (node) node.scrollIntoView({ block: "start" });
  }, [hash, sections]);

  const handleRunTournament = (e) => {
    track("create_tournament_click", {
      location: analyticsLocation,
    });
    if (!user) {
      e.preventDefault();
      setShowAuthModal(true);
    }
  };

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        ogType="website"
        ogImage={screenshot?.src || ogImage}
        ogImageAlt={screenshot?.alt || ogImageAlt}
        structuredData={
          structuredData ||
          generateWebPageStructuredData({
            name: heading,
            description,
            url: canonicalUrl,
          })
        }
        additionalStructuredData={additionalStructuredData}
      />

      <div className="min-h-screen bg-brand-cream">
        <section className="bg-brand-charcoal text-brand-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <p
              className={`font-display tracking-widest uppercase text-sm mb-3 ${
                kicker ? "text-brand-gold" : "text-brand-mist"
              }`}
            >
              {kicker || "Bowling Tournament App"}
            </p>
            <h1 className="font-display font-bold tracking-tight text-4xl md:text-5xl text-brand-cream leading-tight max-w-4xl">
              {heading}
            </h1>
            <p className="mt-4 text-base md:text-lg text-brand-mist max-w-2xl">
              {lede}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/create-tournament"
                onClick={handleRunTournament}
                className="btn-brand md:px-8 md:py-3"
              >
                Run a tournament
              </Link>
              {secondaryTo && secondaryLabel ? (
                <Link
                  to={secondaryTo}
                  onClick={onSecondaryClick}
                  className="inline-flex items-center justify-center rounded-lg bg-brand-cream text-brand-charcoal font-display font-bold tracking-wide px-4 py-2 md:px-8 md:py-3 hover:bg-white transition-colors"
                >
                  {secondaryLabel}
                </Link>
              ) : null}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <ProductBreadcrumb pathname={pathname} />
            </div>
            {importStep ? <ProductImportStep step={importStep} /> : null}
            {sectionHeading ? (
              <h2 className="heading-display text-3xl md:text-4xl">
                {sectionHeading}
              </h2>
            ) : null}
            {screenshot ? (
              <figure className="mt-10 max-w-4xl">
                <div className="card-board overflow-hidden">
                  <img
                    src={screenshot.src}
                    alt={screenshot.alt}
                    className="w-full"
                    width={1600}
                    height={1000}
                  />
                </div>
              </figure>
            ) : null}
            {sections.length > 0 ? (
              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 ${
                  sectionHeading || screenshot ? "mt-10" : ""
                }`}
              >
                {sections.map((section) => (
                  <div key={section.title} id={section.id}>
                    <h3 className="font-display font-bold tracking-tight text-2xl text-brand-charcoal">
                      {section.title}
                    </h3>
                    <div
                      className="mt-3 h-1 w-12 bg-brand-gold"
                      aria-hidden="true"
                    />
                    <p className="mt-4 text-brand-muted text-base leading-relaxed">
                      {section.body}
                    </p>
                    {section.to ? (
                      <p className="mt-3">
                        <Link
                          to={section.to}
                          className="text-brand-charcoal hover:underline"
                        >
                          {section.linkLabel}
                        </Link>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            {children}
            {showNextStep ? <ProductNextStep pathname={pathname} /> : null}
          </div>
        </section>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
};

export default ProductPage;
